import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, BellRing, CheckCheck, Radar } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, formatRelative, itemDate } from "@/i18n";
import { CategoryIcon } from "@/components/CategoryTag";

const SEEN_KEY = "ntn_last_seen";

export const useLiveFeed = () =>
  useQuery({ queryKey: ["live-notify"], queryFn: async () => (await api.get("/live?limit=8")).data, refetchInterval: 35000 });

export const desktopAlertsOn = () => typeof Notification !== "undefined" && Notification.permission === "granted" && localStorage.getItem("ntn_desktop") === "1";

export function NotificationCenter() {
  const { t, lang, title } = useI18n();
  const { data } = useLiveFeed();
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(SEEN_KEY) || "");
  const [desktop, setDesktop] = useState(desktopAlertsOn());
  const box = useRef(null);
  const items = useMemo(() => data?.items || [], [data]);
  const unread = items.filter((i) => (i.retrieved_at || itemDate(i) || "") > lastSeen).length;

  useEffect(() => {
    if (!lastSeen && items.length) {
      const v = items[items.length - 1].retrieved_at || "";
      localStorage.setItem(SEEN_KEY, v);
      setLastSeen(v);
    }
  }, [items, lastSeen]);

  useEffect(() => {
    const close = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const markRead = () => {
    const v = items[0]?.retrieved_at || new Date().toISOString();
    localStorage.setItem(SEEN_KEY, v);
    setLastSeen(v);
  };

  const enableDesktop = async () => {
    if (typeof Notification === "undefined") return;
    if (desktop) { localStorage.setItem("ntn_desktop", "0"); return setDesktop(false); }
    const p = await Notification.requestPermission();
    if (p === "granted") {
      localStorage.setItem("ntn_desktop", "1");
      setDesktop(true);
      new Notification("NxtTechNews", { body: t("notify.enabledBody"), icon: "/logo.png" });
    }
  };

  return (
    <div ref={box} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        aria-label={t("notify.center")}
        className="relative w-9 h-9 inline-flex items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:text-violet-700 hover:border-violet-300 transition-colors"
      >
        {unread ? <BellRing size={16} className="bell-swing" /> : <Bell size={16} />}
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold inline-flex items-center justify-center live-ring">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="notify-pop fixed inset-x-3 top-[4.5rem] sm:absolute sm:inset-x-auto sm:top-auto sm:end-0 sm:mt-2 sm:w-[340px] rounded-2xl border border-violet-200/60 bg-white/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(76,29,149,0.25)] overflow-hidden z-[80]">
          <div className="relative px-4 py-3 border-b border-slate-100 flex items-center justify-between overflow-hidden">
            <span className="scan-line" />
            <span className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-wider text-violet-700">
              <Radar size={14} className="radar-spin" /> {t("notify.center")}
            </span>
            <button data-testid="notification-mark-read" onClick={markRead} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-violet-700">
              <CheckCheck size={13} /> {t("notify.markRead")}
            </button>
          </div>
          <ul className="max-h-[360px] overflow-y-auto divide-y divide-slate-100">
            {items.map((i) => {
              const isNew = (i.retrieved_at || "") > lastSeen;
              return (
                <li key={i.id}>
                  <Link data-testid="notification-item" to={`/article/${i.id}`} onClick={() => setOpen(false)} className="flex gap-3 px-4 py-3 hover:bg-violet-50/60 transition-colors">
                    <span className={`mt-0.5 shrink-0 w-8 h-8 rounded-lg inline-flex items-center justify-center ${isNew ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                      <CategoryIcon slug={i.categories?.[0]} size={15} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-slate-800 leading-snug line-clamp-2">{title(i)}</span>
                      <span className="block mt-0.5 text-[11px] text-muted-foreground">{i.source_name} · {formatRelative(itemDate(i), lang)}</span>
                    </span>
                    {isNew && <span className="ms-auto mt-1.5 w-2 h-2 rounded-full bg-violet-600 shrink-0 live-ring" />}
                  </Link>
                </li>
              );
            })}
          </ul>
          {typeof Notification !== "undefined" && (
            <button data-testid="notification-desktop-toggle" onClick={enableDesktop} className="w-full px-4 py-2.5 text-[12px] font-semibold text-violet-700 bg-violet-50/70 hover:bg-violet-100 transition-colors border-t border-violet-100">
              {desktop ? t("notify.desktopOff") : t("notify.desktopOn")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
